/**
 * Cruise day-icon visibility — mirrors iOS DayPlanningIconsCruiseRules.
 */

import type { Trip } from '../types/client';
import { isAtSeaLocation } from '../services/cruiseItinerariesApi';
import {
  cruiseCityStayContaining,
  cruiseCityStays,
  cruiseVisitRefsFromTrip,
  type CruiseCityStay,
} from './cruiseCityStayGrouping';

function embarkationCityStay(trip: Trip): CruiseCityStay | null {
  const visits = cruiseVisitRefsFromTrip(trip);
  const embark = visits.find((v) => v.isEmbarkation);
  if (embark) return cruiseCityStayContaining(trip, embark.id);

  const sorted = visits
    .slice()
    .sort((a, b) => (a.arrivalDate || '').localeCompare(b.arrivalDate || ''));
  const first = sorted.find((v) => !isAtSeaLocation(v.cityName));
  return first ? cruiseCityStayContaining(trip, first.id) : null;
}

function debarkationCityStay(trip: Trip): CruiseCityStay | null {
  const visits = cruiseVisitRefsFromTrip(trip);
  const debarks = visits.filter((v) => v.isDebarkation);
  const debark = debarks[debarks.length - 1];
  if (debark) return cruiseCityStayContaining(trip, debark.id);

  const sorted = visits
    .slice()
    .sort((a, b) => (a.arrivalDate || '').localeCompare(b.arrivalDate || ''));
  const ports = sorted.filter((v) => !isAtSeaLocation(v.cityName));
  const last = ports[ports.length - 1];
  return last ? cruiseCityStayContaining(trip, last.id) : null;
}

/** True when this city visit should show the cruise airplane planning icon. */
export function shouldShowFlight(trip: Trip, cityVisitId: string): boolean {
  if (trip.tripType !== 'cruise') return true;

  const embarkStay = embarkationCityStay(trip);
  if (embarkStay?.visits[0]?.id === cityVisitId) return true;

  const debarkStay = debarkationCityStay(trip);
  const lastDebark = debarkStay?.visits[debarkStay.visits.length - 1];
  if (lastDebark?.id === cityVisitId) return true;

  return false;
}

/** Hide airplane except first embark-city day and last debark-city day. */
export function hideFlight(trip: Trip, cityVisitId: string): boolean {
  if (trip.tripType !== 'cruise') return false;
  return !shouldShowFlight(trip, cityVisitId);
}

/** Exported for tests — stay count diagnostics. */
export function cruiseStayCount(trip: Trip): number {
  return cruiseCityStays(trip).length;
}
